import { DistanceUnit, WorkoutExerciseType } from "../models/workoutPlanViewModel";

/**
 * How set values read on screen. Time is stored as total seconds and distance as metres;
 * these helpers turn them into what a person types and reads ("2:15", "1.5км").
 */

/** Rounds to at most `digits` decimals and drops trailing zeros: 1.50 → "1.5", 2 → "2". */
export const trimDecimal = (value: number, digits = 2): string =>
  String(Number(value.toFixed(digits)));

/** "45с" under a minute, "2:15" under an hour, "1:02:05" from an hour up. */
export const formatDuration = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.round(totalSeconds));
  if (safe < 60) return `${safe}с`;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const ss = String(seconds).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${ss}`
    : `${minutes}:${ss}`;
};

/** Total seconds as the two inputs show them: whole minutes and the remaining seconds. */
export const splitDuration = (totalSeconds: number) => {
  const safe = Math.max(0, Math.round(totalSeconds));
  return { minutes: Math.floor(safe / 60), seconds: safe % 60 };
};

/** Metres in the exercise's unit: "800м" or "1.5км". */
export const formatDistance = (meters: number, unit: DistanceUnit): string =>
  unit === DistanceUnit.Kilometers
    ? `${trimDecimal(meters / 1000)}км`
    : `${meters}м`;

export const formatSpeed = (speedKmh: number): string => `${trimDecimal(speedKmh, 1)}км/ч`;

/** Running pace for a speed: 12 km/h → "5:00/км". Null when there is no speed. */
export const formatPace = (speedKmh: number | null | undefined): string | null => {
  if (!speedKmh || speedKmh <= 0) return null;
  const secondsPerKm = Math.round(3600 / speedKmh);
  const minutes = Math.floor(secondsPerKm / 60);
  const seconds = String(secondsPerKm % 60).padStart(2, "0");
  return `${minutes}:${seconds}/км`;
};

interface ISetSummaryValues {
  kg: number | null;
  reps: number | null;
  speedKmh?: number | null;
}

/**
 * One set in a few characters, in its exercise's measurement system — the ПРЕДИШНА column
 * and the picker's "последно" pill. Null when there is nothing to show.
 */
export const formatSetSummary = (
  type: WorkoutExerciseType,
  distanceUnit: DistanceUnit,
  { kg, reps, speedKmh }: ISetSummaryValues,
): string | null => {
  switch (type) {
    case WorkoutExerciseType.Time:
      return reps !== null ? formatDuration(reps) : null;
    case WorkoutExerciseType.TimePace: {
      if (reps === null) return null;
      return speedKmh ? `${formatDuration(reps)} · ${formatSpeed(speedKmh)}` : formatDuration(reps);
    }
    case WorkoutExerciseType.Distance:
      return kg !== null && reps !== null
        ? `${kg}кг × ${formatDistance(reps, distanceUnit)}`
        : null;
    case WorkoutExerciseType.KgReps:
    default:
      return kg !== null && reps !== null ? `${kg}кг × ${reps}` : null;
  }
};
